import React from 'react'
import useSWRMutation from 'swr/mutation'

import { useI18N } from '../../../libraries/i18n/hook'
import { QualificationLevel, EducationLevel, UserStudies } from '../../../api/users/types'
import AcademicBachelorIcon from '../../../assets/icons/academic-bachelor.svg?react'
import AcademicMasterIcon from '../../../assets/icons/academic-master.svg?react'
import AcademicGraduatedIcon from '../../../assets/icons/academic-graduated.svg?react'
import CheckIcon from '../../../assets/icons/checked.svg?react'
import UncheckedIcon from '../../../assets/icons/unchecked.svg?react'
import useForm from '../../../libraries/form/hook'
import { updateUserStudies } from '../../../api/users'
import ErrorMessage from '../../components/errors/ErrorMessage'
import SimpleSelectBox from '../form/SimpleSelectBox'
import SelectInput from '../../components/form/SelectInput'
import TextInput from '../../components/form/TextInput'
import FormGroup from '../../components/form/FormGroup'
import RadioButton from '../../components/form/RadioButton'
import FloatingSubmitButton from './FloatingSubmitButton'
import config from '../../../config'

type Props = { onSuccess?: () => void, defaultValues?: Partial<UserStudies> }
const SudiesForm: React.FC<Props> = ({ onSuccess, defaultValues }) => {
  const { t } = useI18N()

  const { fields, error, submitting, handleChange, handleSubmit } = useForm<Partial<UserStudies>, UserStudies, '/users/studies'>({
    initialValues: defaultValues ?? { educationLevel: EducationLevel.STUDYING_BACHELOR, doneProfessionalInternships: false },
    mutation: useSWRMutation('/users/studies', updateUserStudies, { populateCache: true }),
    onSuccess,
  })

  const qualificationLevelOptions = [{ label: t('common.bachelor'), value: QualificationLevel.BACHELOR.toString() }, { label: t('common.bachelor.and.master'), value: QualificationLevel.BACHELOR_AND_MASTER.toString() }]
  return (
    <form onSubmit={handleSubmit} className='pb-10 sm:pb-14'>
      {/* radio button: level */}
      <FormGroup label={t('onboarding.studies.academicLevel')}>
        <div className='flex gap-x-6'>
          <RadioButton
            onChange={handleChange} name='educationLevel' value={EducationLevel.STUDYING_BACHELOR}
            defaultChecked={defaultValues?.educationLevel === EducationLevel.STUDYING_BACHELOR}
            checked={fields?.educationLevel === EducationLevel.STUDYING_BACHELOR}
            icon={<AcademicBachelorIcon />}
            label={t('onboarding.studies.studying.bachelor')}
            labelPosition='bottom'
          />
          <RadioButton
            onChange={handleChange} name='educationLevel' value={EducationLevel.STUDYING_MASTER}
            checked={fields?.educationLevel === EducationLevel.STUDYING_MASTER} icon={<AcademicMasterIcon />}
            label={t('onboarding.studies.studying.master')}
            labelPosition='bottom'
          />
          <RadioButton
            onChange={handleChange} name='educationLevel' value={EducationLevel.GRADUATED}
            checked={fields?.educationLevel === EducationLevel.GRADUATED} icon={<AcademicGraduatedIcon />}
            label={t('onboarding.studies.graduated')}
            labelPosition='bottom'
          />
        </div>
      </FormGroup>
      {EducationLevel.GRADUATED === fields.educationLevel && (
        <FormGroup>
          <SelectInput
            label={t('onboarding.qualificationLevel')} name='qualificationLevel'
            value={fields.qualificationLevel ? qualificationLevelOptions.find(qualificationLevel => qualificationLevel.value === fields.qualificationLevel) : undefined}
            onChange={(value) => handleChange({ target: { name: 'qualificationLevel', value } } as React.ChangeEvent<HTMLInputElement>)}
            options={qualificationLevelOptions}
            isMulti={false}
            required
          />
        </FormGroup>
      )}
      {/* GET /api/universities */}
      {/* select box: bachelorUniversityId */}
      <FormGroup>
        <SimpleSelectBox label={t('common.bachelor.university')} apiUrl='/universities' name='bachelorUniversityId' value={fields.bachelorUniversityId?.toString()} handleChange={handleChange} alwaysVisibleLabels={config.ALWAYS_VISIBLE_UNIVERSITIES} required />
      </FormGroup>

      {/* GET /api/universities/bachelors */}
      {/* select box: bachelorId */}
      <FormGroup>
        <SimpleSelectBox label={t('common.bachelor.name')} apiUrl='/universities/bachelors' name='bachelorId' value={fields.bachelorId?.toString()} handleChange={handleChange} alwaysVisibleLabels={config.ALWAYS_VISIBLE_BACHELORS} required />
      </FormGroup>

      {/* select box: bachelorStartYear */}
      {/* current year minus 7 */}
      {EducationLevel.STUDYING_BACHELOR === fields.educationLevel && (
        <FormGroup>
          <BachelorStartYearSelectBox label={t('common.bachelor.year.start')} value={fields.bachelorStartYear?.toString()} onChange={(value) => handleChange({ target: { name: 'bachelorStartYear', value } } as React.ChangeEvent<HTMLInputElement>)} required />
        </FormGroup>
      )}

      {EducationLevel.STUDYING_MASTER === fields.educationLevel && (
        <>
          <FormGroup>
            <SimpleSelectBox
              label={t('common.master.university')} apiUrl='/universities'
              name='masterUniversityId' value={fields.masterUniversityId?.toString()} handleChange={handleChange}
              required
            />
          </FormGroup>
          <FormGroup>
            <TextInput className='w-full' value={fields.masterName} label={t('common.master.name')} placeholder={t('common.master.name')} name='masterName' onChange={handleChange} required/>
          </FormGroup>
          <FormGroup>
            <SelectInput
              label={t('common.master.year.end')} name='masterEndYear'
              value={fields.masterEndYear ? { value: fields.masterEndYear.toString(), label: fields.masterEndYear.toString() } : undefined}
              onChange={(value) => handleChange({ target: { name: 'masterEndYear', value } } as React.ChangeEvent<HTMLInputElement>)}
              options={Array.from({ length: 4 }, (_, i) => new Date().getFullYear() + i).map(year => ({ label: year.toString(), value: year.toString() }))}
              isMulti={false}
              required
            />
          </FormGroup>
        </>
      )}


      {/* radio button: doneProfessionalInternships */}
      {EducationLevel.GRADUATED === fields.educationLevel && (
        <>
          <FormGroup label={t('onboarding.working.question')}>
            <div className='flex gap-x-6'>
              <RadioButton
                onChange={handleChange} name='isWorking'
                icon={<CheckIcon />} checked={fields.isWorking?.toString() === 'true'} fillType='stroke' label={t('onboarding.working.employed')}
                value='true'
              />
              <RadioButton
                onChange={handleChange} name='isWorking'
                icon={<UncheckedIcon />} checked={fields.isWorking?.toString() === 'false'} fillType='stroke' label={t('onboarding.working.no')}
                value='false'
              />
            </div>
          </FormGroup>
          {/* radio button: internshipIndustryId */}
          {/* GET /api/industries */}
          {fields.isWorking?.toString() === 'true' && (
            <FormGroup>
              <SimpleSelectBox label={t('common.industry.sector')} apiUrl='/industries' name='workingIndustryId' value={fields.workingIndustryId?.toString()} handleChange={handleChange} required />
            </FormGroup>
          )}
        </>
      )}

      <FloatingSubmitButton disabled={submitting}>{t('common.continue')}</FloatingSubmitButton>
      {error && <ErrorMessage error={error} />}
    </form>
  )
}

type BachelorStartYearProps = { value: string | undefined, label: string, onChange: (value: string) => void, required?: boolean }
const BachelorStartYearSelectBox:React.FC<BachelorStartYearProps> = ({ label, value, onChange, required }) => {
  const currentYear = new Date().getFullYear()
  const options = Array.from({ length: 10 }, (_, i) => currentYear - i).map(year => ({ label: year.toString(), value: year.toString() }))
  return (
    <SelectInput
      label={label}
      name='bachelorStartYear'
      value={value ? { value, label: options.find(option => option.value === value)?.label ?? '' } : undefined}
      onChange={onChange}
      options={options}
      isMulti={false}
      required={required}
    />
  )
}
export default SudiesForm
